//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/core/services/service';
import ProductOrdersModel from '../models/orders.model';

/**
 * @service ProductOrdersService
 * @description Service for managing product orders within the Material Management System (MMS).
 * This service facilitates the retrieval of product order data based on the organizational hierarchy
 * of the service provider (Business, Manager, or Technician).
 *
 * It acts as the data layer for order management features, allowing users to view orders
 * generated by themselves or their subordinates depending on their role.
 *
 * **Key Responsibilities:**
 * 1. **Data Retrieval**: Fetching lists of product orders from the backend API.
 * 2. **Role-Based Filtering**: Providing specific methods to get orders by Business ID, Manager ID, or Technician ID.
 * 3. **Resource Management**: Handling the lifecycle of HTTP requests to prevent memory leaks.
 *
 * **Data Flow:**
 * - Components call specific methods based on the logged-in user's context.
 * - The service constructs the appropriate API URL with query parameters.
 * - The backend returns a list of `ProductOrdersModel`.
 * - The service resolves the promise with this data.
 *
 * @feature 1. Retrieve orders for a specific Provider Business.
 * @feature 2. Retrieve orders for a specific Provider Manager.
 * @feature 3. Retrieve orders generated by a specific Technician.
 * @feature 4. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<ProductOrdersModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /mms/productorders/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class ProductOrdersService extends Service<ProductOrdersModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/mms/productorders/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/mms/productorders/');
  }

  /**
   * @method getProductOrdersByProviderBusinessID
   * @async
   * @description Fetch product orders associated with a specific Provider Business ID.
   *
   * Retrieves a large dataset (pageSize=50000) of orders, ordered by creation date ('new').
   *
   * @param {string} providerBusinessID - The unique identifier of the provider business.
   * @returns {Promise<Array<ProductOrdersModel>>} Promise resolving to an array of ProductOrdersModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * productOrdersService.getProductOrdersByProviderBusinessID('bus-123').then(orders => {
   *   console.log('Business Orders:', orders);
   * });
   */
  public getProductOrdersByProviderBusinessID(providerBusinessID: string) {
    return new Promise<Array<ProductOrdersModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<ProductOrdersModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=50000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getProductOrdersByProviderManagerID
   * @async
   * @description Fetch product orders associated with a specific Provider Manager ID.
   *
   * @param {string} providerManagerID - The unique identifier of the provider manager.
   * @returns {Promise<Array<ProductOrdersModel>>} Promise resolving to an array of ProductOrdersModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * productOrdersService.getProductOrdersByProviderManagerID('mgr-456').then(orders => {
   *   console.log('Manager Orders:', orders);
   * });
   */
  public getProductOrdersByProviderManagerID(providerManagerID: string) {
    return new Promise<Array<ProductOrdersModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<ProductOrdersModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=50000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getProductOrdersByProviderTechnicianID
   * @async
   * @description Fetch product orders generated by a specific Provider Technician.
   *
   * Filters the orders using the `generated_by_person_id` query parameter.
   *
   * @param {string} providerTechnicianID - The unique identifier of the technician (person ID).
   * @returns {Promise<Array<ProductOrdersModel>>} Promise resolving to an array of ProductOrdersModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * productOrdersService.getProductOrdersByProviderTechnicianID('tech-789').then(orders => {
   *   console.log('Technician Orders:', orders);
   * });
   */
  public getProductOrdersByProviderTechnicianID(providerTechnicianID: string) {
    return new Promise<Array<ProductOrdersModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<ProductOrdersModel>>(`${this.baseUrl}${this.serviceUrl}?generated_by_person_id=${providerTechnicianID}&pageSize=50000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
